import { useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useFeature } from '@growthbook/growthbook-react';
import { useQueryThemes } from '../../hooks/API/useQueryThemes';
import { useQueryProviders } from '../../hooks/API/useQueryProviders';
import { useQueryCategories } from '../../hooks/API/useQueryCategories';
import { useQueryRecentGame } from '../../hooks/API/useQueryStorageGames';
import { useIsDesktopLayout } from '../../hooks/useIsDesktopLayout';
import { useQueryJackpot } from '../../hooks/API/useQueryJackpot';
import { useQueryMockBigWinsData } from '../../hooks/API/demoMockData';
import { isCentrifugoConnectedSelector } from '../../redux/reducers/domain/centrifugo';
import {
  bigWinsSelector,
  isBigWinsHistoryLoadedSelector,
} from '../../redux/reducers/domain/centrifugo/bigWins';
import FEATURES from '../../constants/featureToggling';

// TODO maybe remove it as it become obsolete
export const useQueryDashboardData = () => {
  const defaultList = useRef([]);
  const defaultCollections = useRef({
    themeList: defaultList.current,
    providerList: defaultList.current,
    categoryList: defaultList.current,
    jackpotList: defaultList.current,
    isLoading: true,
    isSuccess: false,
  });

  const queryThemes = useQueryThemes();
  const queryProviders = useQueryProviders();
  const queryCategories = useQueryCategories();
  const queryJackpots = useQueryJackpot();

  const themeList = queryThemes.data?.items ?? defaultList.current;
  const providerList = queryProviders.data?.items ?? defaultList.current;
  const categoryList = queryCategories.data?.items ?? defaultList.current;
  const jackpotList = queryJackpots.data ?? defaultList.current;

  const isLoading =
    queryThemes.isLoading ||
    queryProviders.isLoading ||
    queryCategories.isLoading ||
    queryJackpots.isLoading;

  const collections = useMemo(
    () => ({
      themeList,
      providerList,
      categoryList,
      jackpotList,
    }),
    [categoryList, providerList, themeList, jackpotList]
  );

  return useMemo(() => {
    return isLoading
      ? defaultCollections.current
      : {
          ...collections,
          isSuccess: true,
          isLoading: false,
        };
  }, [collections, isLoading]);
};

export const useRecentGame = (queryOptions = {}) => {
  const defaultCollection = useRef({
    isLoading: false,
    recentGame: null,
    recentGameId: null,
    recentGameThemeId: null,
    recentGameProviderId: null,
  });
  const queryGame = useQueryRecentGame(queryOptions);
  const recentGame = queryGame?.data;

  if (recentGame) {
    const recentGameId = recentGame?.id;
    const recentGameThemeId = recentGame?.themes?.[0] ?? null;
    const recentGameProviderId = recentGame?.provider;

    return {
      isLoading: queryGame?.isLoading,
      recentGame,
      recentGameId,
      recentGameThemeId,
      recentGameProviderId,
    };
  }

  return defaultCollection.current;
};

export const useQueryMockBigWins = (enabled = false) => {
  const { data, isLoading } = useQueryMockBigWinsData(enabled);
  const isDesktop = useIsDesktopLayout();
  return useMemo(
    () => ({
      data: data?.slice(0, isDesktop ? 5 : 3),
      isLoading,
    }),
    [data, isLoading, isDesktop]
  );
};

export const useQueryRealBigWins = (enabled = false) => {
  const isBigWinsEnabled = useFeature(FEATURES.ENABLE_BIG_WINS_FEED).on;
  const isCfConnected = useSelector(isCentrifugoConnectedSelector);
  const areBigWinsLoaded = useSelector(isBigWinsHistoryLoadedSelector);
  const bigWins = useSelector(bigWinsSelector);
  const isDesktop = useIsDesktopLayout();

  // Used only for mock data mode
  const data = useMemo(
    () => bigWins?.slice(0, isDesktop ? 5 : 3),
    [bigWins, isDesktop]
  );
  return useMemo(
    () => ({
      isLoading: isBigWinsEnabled && isCfConnected && !areBigWinsLoaded,
      data: enabled && isBigWinsEnabled && data?.length ? data : null,
    }),
    [isBigWinsEnabled, isCfConnected, areBigWinsLoaded, data, enabled]
  );
};

export const useQueryBigWins = () => {
  const isDemo = useFeature(FEATURES.USE_MOCK_DATA_MODE).on;
  const realQuery = useQueryRealBigWins(!isDemo);
  const demoQuery = useQueryMockBigWins(isDemo);
  return isDemo ? demoQuery : realQuery;
};
